import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import { interlude } from "@/interlude";
import { ComplianceGate } from "@/pages/channels/pages/emailOnboard/components/ComplianceGate";
import { LiveEmailPreview } from "@/pages/channels/pages/emailOnboard/components/LiveEmailPreview";
import { findDuplicateEmailLocals } from "@/pages/channels/pages/emailOnboard/utils/findDuplicateEmailLocals";
import { isFreeEmailDomain } from "@/pages/channels/pages/emailOnboard/utils/isFreeEmailDomain";

const MAX_SENDER_PROFILES = 2;

const DNS_PROVIDERS = [
  "Cloudflare",
  "GoDaddy",
  "Google Cloud",
  "AWS Route 53",
  "Other",
  "Unknown",
] as const;

// ── Styles matching demo (styles.css + inline styles) ──

const styles = {
  formSection: {
    transition: "opacity 0.2s ease",
  },
  formSectionLocked: {
    opacity: 0.45,
    pointerEvents: "none" as const,
  },
  // domain-layout: 2-column grid
  domainLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 360px) minmax(0, 420px)",
    gap: "28px",
    alignItems: "start",
  },
  // field-label
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: theme.colors.staticFgBody,
    marginBottom: "6px",
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px",
  },
  requiredMark: {
    color: theme.colors.staticFgNegative,
  },
  optionalMark: {
    marginLeft: "4px",
    color: theme.colors.staticFgNote,
    fontSize: "10px",
    fontWeight: 600,
  },
  // profile-email-row
  emailRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  // profile-email-domain (readonly domain suffix)
  domainSuffix: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 40,
    padding: "0 12px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgContainer,
    color: theme.colors.colorNeutral80,
    fontSize: "12px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "nowrap",
  },
  // sender-profile-editor
  profileEditor: {
    padding: "14px 14px 12px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.staticBgContainer,
  },
  // sn-tab (profile pill tab)
  profileTab: {
    padding: "3px 12px",
    borderRadius: "20px",
    border: `1.5px solid ${theme.colors.staticFgLine}`,
    backgroundColor: theme.colors.white,
    color: theme.colors.staticFgNote,
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    minWidth: "auto",
    lineHeight: 1.5,
    transition: "border-color 0.15s, background 0.15s, color 0.15s",
    "&:hover": {
      borderColor: theme.colors.colorBlue30,
      color: theme.colors.colorBlue50,
    },
  },
  profileTabActive: {
    borderColor: theme.colors.colorBlue50,
    backgroundColor: theme.colors.colorBlue10,
    color: theme.colors.colorBlue50,
    fontWeight: 600,
  },
  // sn-tab-with-remove — tab with × button
  profileTabWithRemove: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    paddingRight: "8px",
  },
  // sn-tab-remove (× circle)
  profileTabRemove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "999px",
    backgroundColor: "rgba(123, 133, 148, 0.12)",
    color: "inherit",
    fontSize: "11px",
    lineHeight: 1,
    cursor: "pointer",
    flexShrink: 0,
    border: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "rgba(238, 154, 119, 0.16)",
      color: "#ee9a77",
    },
  },
  // field-hint
  fieldHint: {
    color: theme.colors.staticFgNote,
    fontSize: "10px",
    marginTop: "4px",
  },
  // MUI TextField overrides to match demo text-input
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "12px",
      "& fieldset": {
        borderColor: theme.colors.staticFgLine,
      },
      "&:hover fieldset": {
        borderColor: theme.colors.colorBlue30,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.colors.colorBlue50,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 12px",
      height: "auto",
      color: theme.colors.staticFgTitle,
      fontSize: "12px",
      "&::placeholder": {
        color: theme.colors.staticFgPlaceholder,
        opacity: 1,
      },
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

// ── Validation helpers ──

const BRAND_DOMAIN_REGEX = /^(?!-)[a-z\d-]+(?:\.[a-z\d-]+)*\.[a-z]{2,}$/;

const isBrandDomainValid = (value: string): boolean => {
  if (!value) return false;
  const lower = value.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return false;
  if (lower.includes("/")) return false;
  if (lower.startsWith("-") || lower.endsWith("-")) return false;
  return BRAND_DOMAIN_REGEX.test(lower);
};

// Sub domain prefix — one or more DNS labels (e.g. "marketing" or "mail.send").
// The backend requires the resulting sender domain to be a strict subdomain of
// the brand domain, so an empty prefix is not allowed.
const SUB_DOMAIN_REGEX =
  /^[a-z\d](?:[a-z\d-]*[a-z\d])?(?:\.[a-z\d](?:[a-z\d-]*[a-z\d])?)*$/;

const isSubDomainValid = (value: string): boolean => {
  const lower = value.trim().toLowerCase();
  if (!lower) return false;
  return SUB_DOMAIN_REGEX.test(lower);
};

// ── Types ──

interface SenderProfile {
  name: string;
  emailLocal: string;
}

/**
 * Form pre-fill / readonly hint passed from the page.
 *
 * - `status` mirrors the legacy onboard-flow shape so the form's pre-fill /
 *   readonly logic keeps working unchanged. The page derives it from the
 *   underlying Channel + SenderDomain state.
 * - `brandDomain` is FE-only (the backend does not persist it); the page
 *   may pass undefined when no historical value is available.
 * - `senderProfilesData[].emailLocal` is the part before `@` in
 *   `ChannelEmailSenderProfile.from_email`; the page splits the value.
 */
type ApplicationData = {
  status: "pending_dns" | "active";
  brandDomain?: string;
  senderDomain: string;
  dnsProvider?:
    | "cloudflare"
    | "godaddy"
    | "google_cloud"
    | "google_domains"
    | "aws_route53"
    | "other"
    | "unknown";
  /** Pre-fill source for sender-profile rows. New flow omits it. */
  senderProfilesData?: Array<{ name: string; emailLocal: string }>;
  /** Pre-fill source for the reply-to field. New flow omits it. */
  replyToEmail?: string;
};

interface DomainSetupTabProps {
  onJumpToTab: (tab: "dns-verify") => void;
  /**
   * Fired after `POST /channels/` succeeds, with the new channel's id so
   * the parent can pin the page to this channel and refetch its sender
   * domain to drive the DNS verify tab.
   */
  onApplicationCreated?: ((channelId: number) => void) | undefined;
  application?: ApplicationData | null | undefined;
}

// ── Component ──

export const DomainSetupTab: React.FC<DomainSetupTabProps> =
  function DomainSetupTab({ onJumpToTab, onApplicationCreated, application }) {
    const { t } = useTranslation();

    // The domain + profiles are locked in as soon as the channel is created
    // (pending_dns), not just after verification — editing isn't supported
    // either way, so the form is read-only whenever an application exists.
    const isReadonly = useMemo(() => {
      if (!application) return false;
      return (
        application.status === "active" || application.status === "pending_dns"
      );
    }, [application]);

    // Compliance Gate — auto-checked if application already exists
    const [gateValues, setGateValues] = useState<[boolean, boolean, boolean]>(
      () => (application ? [true, true, true] : [false, false, false]),
    );
    const allGateChecked = useMemo(
      () => isReadonly || gateValues.every(Boolean),
      [isReadonly, gateValues],
    );

    const handleGateChange = useHandler((index: number, checked: boolean) => {
      if (isReadonly) return;
      setGateValues((prev) => {
        const next = [...prev] as [boolean, boolean, boolean];
        next[index] = checked;
        return next;
      });
    });

    // Extract subdomain from senderDomain (e.g. "edm.example.com" with brandDomain "example.com" → "edm")
    const extractSubDomain = useCallback(
      (senderDomain: string, brand: string): string => {
        if (senderDomain === brand) return "";
        if (senderDomain.endsWith(`.${brand}`)) {
          return senderDomain.slice(0, -(brand.length + 1));
        }
        return "";
      },
      [],
    );

    // Form state — initialize from application if available
    const [brandDomain, setBrandDomain] = useState(
      () => application?.brandDomain ?? "",
    );
    const [brandDomainTouched, setBrandDomainTouched] = useState(false);
    const [subDomain, setSubDomain] = useState(() =>
      application?.brandDomain
        ? extractSubDomain(application.senderDomain, application.brandDomain)
        : "",
    );
    const [subDomainTouched, setSubDomainTouched] = useState(false);
    const [replyToLocal, setReplyToLocal] = useState(() => {
      if (!application?.replyToEmail) return "";
      const atIndex = application.replyToEmail.indexOf("@");
      return atIndex > 0
        ? application.replyToEmail.slice(0, atIndex)
        : application.replyToEmail;
    });
    // replyToDomainOverride: only set when user manually edits the domain
    const [replyToDomainOverride, setReplyToDomainOverride] = useState<
      string | null
    >(() => {
      if (!application?.replyToEmail) return null;
      const atIndex = application.replyToEmail.indexOf("@");
      if (atIndex <= 0) return null;
      const domain = application.replyToEmail.slice(atIndex + 1);
      // If it matches brandDomain, no override needed
      return domain === application.brandDomain ? null : domain;
    });
    const [replyToTouched, setReplyToTouched] = useState(false);

    const replyToDomain =
      replyToDomainOverride !== null ? replyToDomainOverride : brandDomain;

    const replyToEmail = useMemo(() => {
      if (!replyToLocal) return "";
      const domain = replyToDomain || "";
      return domain ? `${replyToLocal}@${domain}` : replyToLocal;
    }, [replyToLocal, replyToDomain]);

    const replyToDomainError = useMemo(() => {
      if (!replyToDomainOverride || !brandDomain) return "";
      if (
        replyToDomainOverride !== brandDomain &&
        !replyToDomainOverride.endsWith(`.${brandDomain}`)
      ) {
        return t(
          "emailOnboard.domainSetup.replyTo.domainError",
          "網域必須是 {{brandDomain}} 或其子網域",
          { brandDomain },
        );
      }
      return "";
    }, [replyToDomainOverride, brandDomain, t]);
    const [dnsProvider, setDnsProvider] = useState(() => {
      if (!application?.dnsProvider) return "Cloudflare";
      const map: Record<string, string> = {
        cloudflare: "Cloudflare",
        godaddy: "GoDaddy",
        google_cloud: "Google Cloud",
        google_domains: "Google Cloud",
        aws_route53: "AWS Route 53",
        other: "Other",
        unknown: "Unknown",
      };
      return map[application.dnsProvider] ?? "Cloudflare";
    });
    const [dnsProviderOther, setDnsProviderOther] = useState(() => {
      if (application?.dnsProvider === "other") return "";
      return "";
    });
    const [activeProfileIndex, setActiveProfileIndex] = useState(0);
    const [profiles, setProfiles] = useState<Array<SenderProfile>>(() => {
      if (
        application?.senderProfilesData &&
        application.senderProfilesData.length > 0
      ) {
        return application.senderProfilesData.map((p) => ({
          name: p.name,
          emailLocal: p.emailLocal,
        }));
      }
      return [{ name: "", emailLocal: "" }];
    });
    const [isSubmitted, setIsSubmitted] = useState(() => isReadonly);

    // Derived
    const effectiveDomain = useMemo(() => {
      // Once the channel exists, the persisted sender domain is authoritative
      // (brandDomain is FE-only and not persisted, so it can't be recomposed
      // from the brand/sub fields here).
      if (application?.senderDomain) return application.senderDomain;
      if (!brandDomain) return "";
      return subDomain ? `${subDomain}.${brandDomain}` : brandDomain;
    }, [application?.senderDomain, brandDomain, subDomain]);

    const domainSuffix = useMemo(() => {
      if (!brandDomain) return ".your-brand-domain.com";
      return `.${brandDomain}`;
    }, [brandDomain]);

    const emailDomainSuffix = useMemo(() => {
      if (!effectiveDomain) return "@your-brand-domain.com";
      return `@${effectiveDomain}`;
    }, [effectiveDomain]);

    // Build preview data for LiveEmailPreview.
    // Matches demo logic: always show a full email using fallback local parts
    // (hello / newsletter) when the user hasn't typed one yet.
    const previewProfiles = useMemo(
      () =>
        profiles.map((p, i) => {
          const fallbackLocal = i === 0 ? "hello" : "newsletter";
          const local = p.emailLocal || fallbackLocal;
          const domain = effectiveDomain || "your-brand-domain.com";
          return {
            name: p.name,
            email: `${local}@${domain}`,
            replyTo: replyToEmail || "no-reply@example.com",
          };
        }),
      [profiles, effectiveDomain, replyToEmail],
    );

    // Validation
    const brandDomainError = useMemo(() => {
      if (!brandDomainTouched || !brandDomain) return "";
      if (!isBrandDomainValid(brandDomain)) {
        return t(
          "emailOnboard.domainSetup.brandDomain.error",
          "請輸入有效的網域格式（例：cresclab.site）",
        );
      }
      if (isFreeEmailDomain(brandDomain)) {
        return t(
          "emailOnboard.domainSetup.brandDomain.errorFreeDomain",
          "無法使用 Gmail、Yahoo 等免費信箱網域。請改用貴公司可自行設定 DNS 的網域，才能完成寄件驗證。",
        );
      }
      return "";
    }, [brandDomain, brandDomainTouched, t]);

    const subDomainError = useMemo(() => {
      if (!subDomainTouched) return "";
      if (!subDomain.trim()) {
        return t(
          "emailOnboard.domainSetup.subDomain.errorRequired",
          "請輸入子網域前綴（例：marketing），寄件網域必須是 Brand Domain 的子網域",
        );
      }
      if (!isSubDomainValid(subDomain)) {
        return t(
          "emailOnboard.domainSetup.subDomain.errorFormat",
          "子網域格式不正確，僅能使用英數字與連字號（-）",
        );
      }
      return "";
    }, [subDomain, subDomainTouched, t]);

    const replyToError = useMemo(() => {
      if (!replyToTouched || !replyToLocal) return "";
      if (!replyToLocal.trim()) {
        return t(
          "emailOnboard.domainSetup.replyTo.error",
          "請輸入有效的 Email 格式",
        );
      }
      return "";
    }, [replyToLocal, replyToTouched, t]);

    // Every profile shares one sender domain, so two identical local parts
    // compose the same from_email. The backend rejects that mid-transaction and
    // rolls the whole channel creation back as an opaque 500, so block it here.
    const duplicateProfileIndexes = useMemo(
      () => findDuplicateEmailLocals(profiles),
      [profiles],
    );

    // Shown on whichever colliding profile the user is currently viewing —
    // every participant in a collision is flagged, so the error stays visible
    // no matter which sender tab they switch to.
    const senderEmailError = useMemo(() => {
      if (!duplicateProfileIndexes.has(activeProfileIndex)) return "";
      return t(
        "emailOnboard.domainSetup.senderEmail.errorDuplicate",
        "寄件地址不能與其他寄件者重複",
      );
    }, [activeProfileIndex, duplicateProfileIndexes, t]);

    const canSubmit = useMemo(() => {
      if (!allGateChecked) return false;
      if (!isBrandDomainValid(brandDomain)) return false;
      if (isFreeEmailDomain(brandDomain)) return false;
      if (!isSubDomainValid(subDomain)) return false;
      const hasValidProfile = profiles.some(
        (p) => p.name.trim() && p.emailLocal.trim(),
      );
      if (!hasValidProfile) return false;
      if (duplicateProfileIndexes.size > 0) return false;
      if (!replyToLocal.trim()) return false;
      if (replyToDomainError) return false;
      if (!replyToDomain) return false;
      if (!dnsProvider) return false;
      return true;
    }, [
      allGateChecked,
      brandDomain,
      subDomain,
      profiles,
      duplicateProfileIndexes,
      replyToLocal,
      replyToDomain,
      replyToDomainError,
      dnsProvider,
    ]);

    // Profile management
    const handleAddProfile = useHandler(() => {
      if (profiles.length >= MAX_SENDER_PROFILES) return;
      setProfiles((prev) => [...prev, { name: "", emailLocal: "" }]);
      setActiveProfileIndex(profiles.length);
    });

    const handleRemoveProfile = useHandler((index: number) => {
      if (profiles.length <= 1) return;
      setProfiles((prev) => prev.filter((_, i) => i !== index));
      if (activeProfileIndex >= index && activeProfileIndex > 0) {
        setActiveProfileIndex(activeProfileIndex - 1);
      }
    });

    const updateProfile = useCallback(
      (field: keyof SenderProfile, value: string) => {
        setProfiles((prev) =>
          prev.map((p, i) =>
            i === activeProfileIndex ? { ...p, [field]: value } : p,
          ),
        );
      },
      [activeProfileIndex],
    );

    const handlePreviewSlotClick = useHandler((index: number) => {
      setActiveProfileIndex(index);
    });

    // Submit mutation — wired to the unified POST /channels endpoint.
    // `dnsProvider` and the three compliance checkboxes are FE-only UX
    // signals (used for tailored DNS instructions and gating the submit
    // button); the backend does not persist them.
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const createChannelMutation = interlude.channel.useCreateChannel(
      undefined,
      {
        onSuccess: (createdChannel) => {
          setIsSubmitted(true);
          setIsSubmitting(false);
          onApplicationCreated?.(createdChannel.id);
        },
        onError: async (error) => {
          setIsSubmitting(false);
          const handled = await handleNonGlobalApiError(error, {
            CHANNEL_ALREADY_EXISTS() {
              enqueueSnackbar(
                t(
                  "emailOnboard.domainSetup.error.channelAlreadyExists",
                  "此品牌網域已建立過 Email 寄件網域，請勿重複建立",
                ),
                { variant: "error" },
              );
            },
          });
          if (!handled) {
            enqueueSnackbar(
              t("common.apiError.unexpectedError", "系統發生錯誤，請稍後再試"),
              { variant: "error" },
            );
          }
        },
      },
    );

    const handleSubmit = useHandler(() => {
      if (!canSubmit || isSubmitting || createChannelMutation.isPending) return;
      // Filter out incomplete profile rows so the backend only sees the
      // 1-2 entries the user actually filled in.
      const completedProfiles = profiles.filter(
        (p) => p.name.trim() && p.emailLocal.trim(),
      );
      if (completedProfiles.length === 0) return;
      setIsSubmitting(true);
      createChannelMutation.mutate({
        type: "email",
        externalChannelId: effectiveDomain,
        extra: {
          brandDomain,
          senderProfiles: completedProfiles.map((p) => ({
            name: p.name.trim(),
            fromEmail: `${p.emailLocal.trim()}@${effectiveDomain}`,
            replyTo: replyToEmail,
          })),
        },
      });
    });

    const activeProfile = profiles[activeProfileIndex];

    return (
      <Stack gap="24px">
        <ComplianceGate values={gateValues} onChange={handleGateChange} />

        {isSubmitted ? (
          <InfoBox
            variant="success"
            title={t(
              "emailOnboard.domainSetup.success.title",
              "已建立寄件網域",
            )}
            description={
              <Stack alignItems="flex-start" gap="8px">
                {t(
                  "emailOnboard.domainSetup.success.description",
                  "接下來請前往 DNS 驗證，將系統產生的 DNS Record 提供給 IT 或工程師設定。",
                )}
                {/* Text link on its own line under the description, instead of
                    the InfoBox `action` slot which pins it to the far right.
                    Matches the InfoBox action link style (underlined, success
                    color). */}
                <Typography
                  variant="body"
                  fontWeight="medium"
                  onClick={() => onJumpToTab("dns-verify")}
                  css={css({
                    textDecoration: "underline",
                    cursor: "pointer",
                    color: theme.colors.staticFgPositive,
                  })}
                >
                  {t(
                    "emailOnboard.domainSetup.success.gotoDns",
                    "前往 DNS 驗證",
                  )}
                </Typography>
              </Stack>
            }
          />
        ) : null}

        <Box
          sx={{
            ...styles.formSection,
            ...(!allGateChecked && styles.formSectionLocked),
          }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            css={css({ marginBottom: 14, color: theme.colors.staticFgTitle })}
          >
            {t("emailOnboard.domainSetup.formTitle", "Email 網域資料")}
          </Typography>

          <Box sx={styles.domainLayout}>
            {/* ── Left column: Form fields ── */}
            <Stack gap="14px">
              {/* After creation the domain is locked in and brandDomain isn't
                  persisted, so show the full sender domain as one read-only
                  field instead of the editable brand/sub inputs. */}
              {isReadonly ? (
                <Box>
                  <Box sx={styles.fieldLabel}>
                    {t(
                      "emailOnboard.domainSetup.senderDomain.label",
                      "寄件網域",
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={effectiveDomain}
                    disabled
                    sx={styles.textField}
                  />
                </Box>
              ) : null}
              {isReadonly ? null : (
                <>
                  {/* Brand Domain */}
                  <Box>
                    <Box sx={styles.fieldLabel}>
                      {t(
                        "emailOnboard.domainSetup.brandDomain.label",
                        "Brand Domain 品牌網域",
                      )}
                      <Box component="span" sx={styles.requiredMark}>
                        *
                      </Box>
                      <Tooltip
                        title={t(
                          "emailOnboard.domainSetup.brandDomain.tooltip",
                          "Email 主要使用的品牌網域，例如公司官方網站網域。請填寫真實存在且可操作 DNS 的網域，後續驗證與寄件設定都會以此為基礎。",
                        )}
                      >
                        <Box component="span" sx={{ display: "flex" }}>
                          <MotifIcon
                            un-i-motif="circle_info"
                            style={{
                              fontSize: 14,
                              color: theme.colors.staticFgNote,
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t(
                        "emailOnboard.domainSetup.brandDomain.placeholder",
                        "例如：cresclab.site",
                      )}
                      value={brandDomain}
                      onChange={(e) =>
                        setBrandDomain(e.target.value.toLowerCase())
                      }
                      onBlur={() => setBrandDomainTouched(true)}
                      error={!!brandDomainError}
                      helperText={brandDomainError}
                      disabled={isReadonly}
                      sx={styles.textField}
                    />
                  </Box>

                  {/* Sub Domain */}
                  <Box>
                    <Box sx={styles.fieldLabel}>
                      {t(
                        "emailOnboard.domainSetup.subDomain.label",
                        "Sub Domain 子網域",
                      )}
                      <Box component="span" sx={styles.requiredMark}>
                        *
                      </Box>
                      <Tooltip
                        title={t(
                          "emailOnboard.domainSetup.subDomain.tooltip",
                          "必填。寄件網域必須是 Brand Domain 的子網域。只需填寫前綴，系統會自動補上 Brand Domain，例如輸入 marketing 會組成 marketing.yourcompany.com。",
                        )}
                      >
                        <Box component="span" sx={{ display: "flex" }}>
                          <MotifIcon
                            un-i-motif="circle_info"
                            style={{
                              fontSize: 14,
                              color: theme.colors.staticFgNote,
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box sx={styles.emailRow}>
                      <TextField
                        size="small"
                        placeholder={t(
                          "emailOnboard.domainSetup.subDomain.placeholder",
                          "例如：marketing",
                        )}
                        value={subDomain}
                        onChange={(e) =>
                          setSubDomain(e.target.value.toLowerCase())
                        }
                        onBlur={() => setSubDomainTouched(true)}
                        error={!!subDomainError}
                        disabled={isReadonly}
                        sx={{ ...styles.textField, flex: 1 }}
                      />
                      <Box sx={styles.domainSuffix}>{domainSuffix}</Box>
                    </Box>
                    {subDomainError ? (
                      <Box
                        sx={{
                          ...styles.fieldHint,
                          color: theme.colors.staticFgNegative,
                        }}
                      >
                        {subDomainError}
                      </Box>
                    ) : null}
                  </Box>
                </>
              )}

              {/* Sender Profile */}
              <Box>
                <Box sx={styles.fieldLabel}>
                  {t(
                    "emailOnboard.domainSetup.senderProfile.label",
                    "Sender Profile 寄件身份",
                  )}
                  <Box component="span" sx={styles.requiredMark}>
                    *
                  </Box>
                  <Tooltip
                    title={t(
                      "emailOnboard.domainSetup.senderProfile.tooltip",
                      "每組寄件身份包含 Sender Name 與 Sender Email。你可以設定最多 2 組，方便對應不同用途或不同內容類型；寄件信箱會使用上方設定的寄件網域。",
                    )}
                  >
                    <Box component="span" sx={{ display: "flex" }}>
                      <MotifIcon
                        un-i-motif="circle_info"
                        style={{
                          fontSize: 14,
                          color: theme.colors.staticFgNote,
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>

                <Box sx={styles.profileEditor}>
                  {/* Profile tab selector (sn-selector) */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="8px"
                    sx={{ mb: "14px" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "11px",
                        fontWeight: 500,
                        color: theme.colors.staticFgNote,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {t(
                        "emailOnboard.domainSetup.senderProfile.switcherLabel",
                        "切換身份",
                      )}
                    </Box>
                    <Stack direction="row" gap="4px">
                      {profiles.map((_, i) => (
                        <Box
                          key={i}
                          component="button"
                          type="button"
                          onClick={() => setActiveProfileIndex(i)}
                          sx={{
                            ...styles.profileTab,
                            ...(i === activeProfileIndex &&
                              styles.profileTabActive),
                            ...(i > 0 &&
                              i === activeProfileIndex &&
                              styles.profileTabWithRemove),
                          }}
                        >
                          {i > 0 && i === activeProfileIndex ? (
                            <>
                              {t("emailOnboard.domainSetup.senderProfile.tab", {
                                defaultValue: "寄件者 {{index}}",
                                index: i + 1,
                              })}
                              <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  handleRemoveProfile(i);
                                }}
                                sx={styles.profileTabRemove}
                              >
                                ✕
                              </Box>
                            </>
                          ) : (
                            t("emailOnboard.domainSetup.senderProfile.tab", {
                              defaultValue: "寄件者 {{index}}",
                              index: i + 1,
                            })
                          )}
                        </Box>
                      ))}
                      {profiles.length < MAX_SENDER_PROFILES ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={handleAddProfile}
                          sx={styles.profileTab}
                        >
                          {t("emailOnboard.domainSetup.senderProfile.addTab", {
                            defaultValue: "+ 寄件者 {{index}}",
                            index: profiles.length + 1,
                          })}
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>

                  {/* Active profile fields */}
                  <Stack gap="14px">
                    <Box>
                      <Box sx={styles.fieldLabel}>
                        {t(
                          "emailOnboard.domainSetup.senderName.label",
                          "Sender Name",
                        )}
                        <Box component="span" sx={styles.requiredMark}>
                          *
                        </Box>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t(
                          "emailOnboard.domainSetup.senderName.placeholder",
                          "例如：漸強實驗室 技術支援",
                        )}
                        value={activeProfile?.name ?? ""}
                        onChange={(e) => updateProfile("name", e.target.value)}
                        disabled={isReadonly}
                        sx={styles.textField}
                      />
                    </Box>
                    <Box>
                      <Box sx={styles.fieldLabel}>
                        {t(
                          "emailOnboard.domainSetup.senderEmail.label",
                          "Sender Email",
                        )}
                        <Box component="span" sx={styles.requiredMark}>
                          *
                        </Box>
                        <Tooltip
                          title={t(
                            "emailOnboard.domainSetup.senderEmail.tooltip",
                            "只需填寫 @ 前方名稱，例如 support；系統會自動組成完整寄件信箱。",
                          )}
                        >
                          <Box component="span" sx={{ display: "flex" }}>
                            <MotifIcon
                              un-i-motif="circle_info"
                              style={{
                                fontSize: 14,
                                color: theme.colors.staticFgNote,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                      <Box sx={styles.emailRow}>
                        <TextField
                          size="small"
                          placeholder={t(
                            "emailOnboard.domainSetup.senderEmail.placeholder",
                            "例如：support",
                          )}
                          value={activeProfile?.emailLocal ?? ""}
                          onChange={(e) =>
                            updateProfile("emailLocal", e.target.value)
                          }
                          error={!!senderEmailError}
                          disabled={isReadonly}
                          sx={{ ...styles.textField, flex: 1 }}
                        />
                        <Box sx={styles.domainSuffix}>{emailDomainSuffix}</Box>
                      </Box>
                      {senderEmailError ? (
                        <Box
                          sx={{
                            ...styles.fieldHint,
                            color: theme.colors.staticFgNegative,
                          }}
                        >
                          {senderEmailError}
                        </Box>
                      ) : (
                        <Box sx={styles.fieldHint}>
                          {t(
                            "emailOnboard.domainSetup.senderEmail.hint",
                            "只需填寫 @ 前方名稱。",
                          )}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Reply-To */}
              <Box>
                <Box sx={styles.fieldLabel}>
                  {t(
                    "emailOnboard.domainSetup.replyTo.label",
                    "Reply-To Email",
                  )}
                  <Box component="span" sx={styles.requiredMark}>
                    *
                  </Box>
                  <Tooltip
                    title={t(
                      "emailOnboard.domainSetup.replyTo.tooltip",
                      "當收件者回覆 Email 時，信件將寄送至此信箱，必須填寫真實存在且可正常收信的信箱。",
                    )}
                  >
                    <Box component="span" sx={{ display: "flex" }}>
                      <MotifIcon
                        un-i-motif="circle_info"
                        style={{
                          fontSize: 14,
                          color: theme.colors.staticFgNote,
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <Box sx={styles.emailRow}>
                  <TextField
                    size="small"
                    placeholder={t(
                      "emailOnboard.domainSetup.replyTo.placeholder",
                      "例如：no-reply",
                    )}
                    value={replyToLocal}
                    onChange={(e) => setReplyToLocal(e.target.value)}
                    onBlur={() => setReplyToTouched(true)}
                    error={!!replyToError}
                    helperText={replyToError}
                    disabled={isReadonly}
                    sx={{ ...styles.textField, flex: 1 }}
                  />
                  <Box
                    component="span"
                    sx={{
                      ...styles.domainSuffix,
                      flexShrink: 0,
                      fontSize: "12px",
                    }}
                  >
                    @
                  </Box>
                  <TextField
                    size="small"
                    placeholder={brandDomain || "your-brand-domain.com"}
                    value={replyToDomain}
                    onChange={(e) => {
                      setReplyToDomainOverride(e.target.value.toLowerCase());
                    }}
                    error={!!replyToDomainError}
                    helperText={replyToDomainError}
                    disabled={isReadonly}
                    sx={{ ...styles.textField, flex: 1 }}
                  />
                </Box>
              </Box>

              {/* DNS Provider */}
              <Box>
                <Box sx={styles.fieldLabel}>
                  {t(
                    "emailOnboard.domainSetup.dnsProvider.label",
                    "DNS 提供商",
                  )}
                  <Box component="span" sx={styles.requiredMark}>
                    *
                  </Box>
                  <Tooltip
                    title={t(
                      "emailOnboard.domainSetup.dnsProvider.tooltip",
                      "請選擇目前管理網域 DNS 設定的服務商；若不清楚可選「我不清楚」，不影響後續設定。",
                    )}
                  >
                    <Box component="span" sx={{ display: "flex" }}>
                      <MotifIcon
                        un-i-motif="circle_info"
                        style={{
                          fontSize: 14,
                          color: theme.colors.staticFgNote,
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={dnsProvider}
                  onChange={(e) => setDnsProvider(e.target.value)}
                  disabled={isReadonly}
                  sx={styles.textField}
                >
                  {DNS_PROVIDERS.map((provider) => (
                    <MenuItem key={provider} value={provider}>
                      {provider === "Unknown"
                        ? t(
                            "emailOnboard.domainSetup.dnsProvider.unknown",
                            "我不清楚（不影響設定）",
                          )
                        : provider === "Other"
                          ? t(
                              "emailOnboard.domainSetup.dnsProvider.other",
                              "其他（請填寫）",
                            )
                          : provider}
                    </MenuItem>
                  ))}
                </TextField>
                {dnsProvider === "Other" ? (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t(
                      "emailOnboard.domainSetup.dnsProvider.otherPlaceholder",
                      "請輸入 DNS 提供商名稱",
                    )}
                    value={dnsProviderOther}
                    onChange={(e) => setDnsProviderOther(e.target.value)}
                    disabled={isReadonly}
                    sx={{ ...styles.textField, mt: "8px" }}
                  />
                ) : null}
              </Box>
            </Stack>

            {/* ── Right column: Live Email Preview (stacked cards) ── */}
            <LiveEmailPreview
              profiles={previewProfiles}
              activeIndex={activeProfileIndex}
              onSlotClick={handlePreviewSlotClick}
            />
          </Box>

          {/* Submit — hidden when readonly */}
          {isReadonly ? null : (
            <Stack direction="row" gap="10px" sx={{ mt: "18px" }}>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting
                  ? t("emailOnboard.domainSetup.submitting", "處理中...")
                  : t("emailOnboard.domainSetup.submitButton", "建立寄件網域")}
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
    );
  };
